import { FormEntryNew, HelpIcon } from '../../formEntryNew';
import { FormBuilderContext } from '../formBuilderContext';
import React from 'react';
import { FieldProps } from './types';
import { observer } from 'mobx-react';
import { LoadingOutlined } from '@ant-design/icons';
import b_ from 'b_';
import './fieldWrapper.less';
import { Property } from '../models';

const b = b_.with('field-wrapper');

interface FieldWrapperProps extends FieldProps {
	children: React.ReactNode;
	className?: string;
	// override the default "write the raw child value straight into store.value[property.id]" behavior — needed
	// when the property's stored value isn't what the child directly emits (e.g. CEAlgorithm's type select emits
	// a type string but the property value is the whole algorithm object built from that type).
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onChange?: (value: any) => void;
	// override the default "read store.value[property.id] straight" behavior — same CEAlgorithm case as onChange
	// above: the type select's displayed value is a nested field of the stored algorithm object, not the raw
	// property value, so without this the select never shows a selected option.
	value?: unknown;
	// shown instead of the caption while the field is unused; once it has a value the header goes back to the caption.
	placeholder?: string;
}

const AdditionalContent = observer(({ property }: { property: Property }) => {
	const store = React.useContext(FormBuilderContext);
	const loading = store.propertyLoading(property);

	const tooltip = property.tooltip;

	return (
		<div className={b('additional-content')}>
			{loading && <LoadingOutlined className={b('loading')} />}
			{tooltip ? <HelpIcon tooltip={tooltip} /> : null}
		</div>
	);
});

export const FieldWrapper = observer(
	({ property, children, className, onChange, value, placeholder }: FieldWrapperProps) => {
		const store = React.useContext(FormBuilderContext);
		const loading = store.propertyLoading(property);
		const renderAdditional = property.tooltip || loading;
		// widened to Record<string, unknown> so TS doesn't try to narrow modelField to the literal 'value' when the
		// override is used — model/modelField genuinely vary in shape between the two branches here.
		const model: Record<string, unknown> = value !== undefined ? { value } : store.value;
		const modelField = value !== undefined ? 'value' : property.id;

		return (
			<FormEntryNew
				headerAdditionalContent={renderAdditional ? <AdditionalContent property={property} /> : null}
				label={store.inline ? null : property.caption}
				placeholder={placeholder}
				model={model}
				onChange={onChange ?? ((value) => store.onPropertyValueChange(property, value))}
				modelField={modelField}
				validator={store.validator.modelValidator}
				disabled={store.propertyDisabled(property)}
				highlight={property.highlight}
				className={className}
			>
				{children}
			</FormEntryNew>
		);
	},
);
